import { PLOT_CHILD_CATEGORY } from "@/config/const";
import Colors from "@/config/Colors.js";
import { List } from "immutable";

export default {
  /** 管理画面用のページネーションの情報を作成する */
  createPrivatePlotPageable({ commit }, payload) {
    const totalElements = payload.enterprisePrivatePlots.length;
    const totalPages = Math.ceil(totalElements / payload.pageSize);
    const pageableInfo = {
      total_elements: totalElements,
      total_pages: totalPages,
    };
    commit("SET_PAGING", pageableInfo);
  },
  createPrivatePlotFeatures({ commit }, payload) {
    const filteredPrivatePlots = payload
      .filter((plot) => plot && plot.created_at)
      .filter((plot) => {
        // 表示期間がないものは表示する
        if (!plot.start_date && !plot.end_date) {
          return true;
        }
        const now = new Date();
        // 表示開始日時に到達してない場合は削る
        if (plot.start_date && now < plot.start_date.toDate()) {
          return false;
        }
        // 表示終了日時を過ぎた場合は削る
        if (plot.end_date && plot.end_date.toDate() < now) {
          return false;
        }
        // 表示期間内のものは表示する
        return true;
      })
      .sort((a, b) => {
        return (
          primaryComparedValueForPrivatePlot(a).toDate() -
          primaryComparedValueForPrivatePlot(b).toDate()
        );
      });

    const features = filteredPrivatePlots.reduce((acc, plot) => {
      // プライベートプロットのFeature(ポイント)
      if (plot.points.length === 1) {
        let pointFeature = {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [plot.points[0].longitude, plot.points[0].latitude],
          },
          properties: {
            hash: plot.hash,
            category: String(
              PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
                .parent,
            ),
            title: plot.title,
            icon: PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
              .icon,
          },
        };
        acc.push(pointFeature);
      }
      // プライベートプロットのFeature(ポリゴン)
      if (plot.points.length > 2) {
        const polygonFeature = {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [pointsForPolygon(plot.points)],
          },
          properties: {
            hash: plot.hash,
            category: String(
              PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
                .parent,
            ),
            title: plot.title,
            color: Colors.getPlotSubCategoryColor(plot.plot_child_category_id),
            icon: PLOT_CHILD_CATEGORY.properties[plot.plot_child_category_id]
              .icon,
          },
        };
        acc.push(polygonFeature);
      }
      return acc;
    }, []);
    commit("SET_FEATURES", features);
  },
  /** プライベートプロットをフェッチする */
  fetchPrivatePlots({ commit }, payload) {
    commit("ADD_PRIVATE_PLOT", payload);
  },

  /** fireStoreでgetしたプライベートプロットを管理する */
  createPrivatePlots({ commit }, payload) {
    commit("SET_PRIVATE_PLOTS", payload);
  },

  /** 削除されたプライベートプロットを除外する */
  removePrivatePlots({ commit }, payload) {
    commit("REMOVE_PRIVATE_PLOTS", payload);
  },

  /** プライベートプロットの座標を追加する */
  addPoint({ commit }, point) {
    commit("ADD_POINT", point);
  },

  /** プライベートプロットの座標を設定する */
  setPoint({ commit }, points) {
    commit("SET_POINT", points);
  },

  /** プライベートプロットの座標をクリアする */
  clear({ commit }) {
    commit("CLEAR_POINT");
  },

  setEditMode({ commit }, mode) {
    commit("SET_EDIT_MODE", mode);
  },

  resetPrivatePlotState({ commit }) {
    let defaultState = {
      pageable: {},
      features: [],
      points: List(),
      editMode: 0,
    };
    commit("RESET_STATE", defaultState);
  },
};

/**
 * PrivatePlotの地図に表示する際の比較値を取得
 * @param {Object} PrivatePlot - PrivatePlotオブジェクト
 * @param {firebase.firestore.Timestamp?} PrivatePlot.start_date - 開始日
 * @param {firebase.firestore.Timestamp} PrivatePlot.created_at - 作成日時
 * @returns {firebase.firestore.Timestamp} 比較値
 */
const primaryComparedValueForPrivatePlot = (
  { start_date, created_at }, // PrivatePlot
) => {
  if (start_date) return start_date;
  return created_at;
};

/**
 * Polygonのための座標配列を生成
 * @param {Array} points - Pointの配列。各要素はlongitudeとlatitudeを持っている必要がある
 * @param {Object} points[0] - Polygonの始点
 * @param {Number} points[index].longitude - 経度
 * @param {Number} points[index].latitude - 緯度
 * @returns {Array} Polygonを閉じた座標配列
 */
const pointsForPolygon = (points = []) => {
  if (!Array.isArray(points)) return [];
  const firstPoint = points[0];
  const pointsForPolygon = points.map(({ longitude, latitude }) => [
    longitude,
    latitude,
  ]);
  pointsForPolygon.push([firstPoint.longitude, firstPoint.latitude]); // Polygonを閉じるために最初のポイントを最後に追加
  return pointsForPolygon;
};
